import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Rocket,
  Play,
  Clock,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Inbox,
  CheckCircle2,
  XCircle,
  Activity,
  BarChart3,
} from 'lucide-react'
import { useNicheStore, type NicheRun, type PipelineParams } from '@/stores/niche'

const syncStatusColors: Record<string, string> = {
  success: 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30',
  failed: 'bg-red-600/20 text-red-400 border-red-500/30',
  running: 'bg-blue-600/20 text-blue-400 border-blue-500/30',
}

function RunCard({ run }: { run: NicheRun }) {
  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-sm font-mono">{run.runId.slice(0, 12)}...</h3>
              <Badge variant="outline" className={syncStatusColors[run.syncStatus] || ''}>
                {run.syncStatus}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {new Date(run.createdAt).toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">{run.weekBucket}</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div className="text-center">
            <p className="text-lg font-bold text-emerald-400">{run.selectedCount}</p>
            <p className="text-xs text-muted-foreground">Selected</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-red-400">{run.rejectedCount}</p>
            <p className="text-xs text-muted-foreground">Rejected</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-blue-400">{run.gtmPackCount}</p>
            <p className="text-xs text-muted-foreground">GTM Packs</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-purple-400">{run.recordCount}</p>
            <p className="text-xs text-muted-foreground">Records</p>
          </div>
        </div>

        {run.selectedNiches.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border">
            {run.selectedNiches.slice(0, 5).map((niche, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {(niche as { name?: string }).name || `Niche ${i + 1}`}
              </Badge>
            ))}
            {run.selectedNiches.length > 5 && (
              <Badge variant="outline" className="text-xs">
                +{run.selectedNiches.length - 5} more
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default function TasksPage() {
  const {
    latestRun,
    history,
    freshness,
    isRunning,
    isLoading,
    error,
    runPipeline,
    fetchLatest,
    fetchHistory,
    fetchFreshness,
  } = useNicheStore()

  const [showRunDialog, setShowRunDialog] = useState(false)
  const [params, setParams] = useState<PipelineParams>({
    seedDomains: [],
    geoTargets: [],
    minMarketSize: 10000,
    launchLimit: 5,
    toneProfile: 'direct',
  })
  const [seedDomainsInput, setSeedDomainsInput] = useState('')
  const [geoTargetsInput, setGeoTargetsInput] = useState('')

  useEffect(() => {
    fetchLatest()
    fetchHistory()
    fetchFreshness()
  }, [fetchLatest, fetchHistory, fetchFreshness])

  const handleRunPipeline = async () => {
    const parsedSeedDomains = seedDomainsInput.split(',').map((s) => s.trim()).filter(Boolean)
    const parsedGeoTargets = geoTargetsInput.split(',').map((s) => s.trim()).filter(Boolean)
    const pipelineParams: PipelineParams = {
      ...params,
      seedDomains: parsedSeedDomains.length > 0 ? parsedSeedDomains : undefined,
      geoTargets: parsedGeoTargets.length > 0 ? parsedGeoTargets : undefined,
    }
    await runPipeline(pipelineParams)
    setShowRunDialog(false)
    fetchLatest()
    fetchHistory()
    fetchFreshness()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Discovery</h1>
          <p className="text-muted-foreground mt-1">
            Niche discovery pipeline and market analysis
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={showRunDialog} onOpenChange={setShowRunDialog}>
            <DialogTrigger asChild>
              <Button size="sm" disabled={isRunning}>
                {isRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Rocket className="h-4 w-4 mr-2" />
                )}
                Run Pipeline
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Run Niche Discovery</DialogTitle>
                <DialogDescription>
                  Configure and launch the niche discovery pipeline
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Seed Domains (comma-separated)</Label>
                  <Textarea
                    value={seedDomainsInput}
                    onChange={(e) => setSeedDomainsInput(e.target.value)}
                    placeholder="example.com, competitor.io"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Geo Targets (comma-separated)</Label>
                  <Input
                    value={geoTargetsInput}
                    onChange={(e) => setGeoTargetsInput(e.target.value)}
                    placeholder="US, UK, CA"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Market Size</Label>
                    <Input
                      type="number"
                      value={params.minMarketSize}
                      onChange={(e) =>
                        setParams({ ...params, minMarketSize: parseInt(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Launch Limit</Label>
                    <Input
                      type="number"
                      value={params.launchLimit}
                      onChange={(e) =>
                        setParams({ ...params, launchLimit: parseInt(e.target.value) || 5 })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tone Profile</Label>
                  <Select
                    value={params.toneProfile}
                    onValueChange={(v) =>
                      setParams({ ...params, toneProfile: v as PipelineParams['toneProfile'] })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Direct</SelectItem>
                      <SelectItem value="consultative">Consultative</SelectItem>
                      <SelectItem value="authority">Authority</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRunDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleRunPipeline} disabled={isRunning}>
                  {isRunning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  Launch
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchLatest()
              fetchHistory()
              fetchFreshness()
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-500">{error}</p>
        </div>
      )}

      {/* Freshness Indicator */}
      {freshness && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm font-medium">Data Freshness</p>
                  <p className="text-xs text-muted-foreground">
                    Last sync: {freshness.lastSyncTimestamp ? new Date(freshness.lastSyncTimestamp).toLocaleString() : 'Never'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={syncStatusColors[freshness.syncStatus] || ''}>
                  {freshness.syncStatus}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {freshness.recordCount} records
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Latest Run */}
      {latestRun && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Latest Run
          </h2>
          <RunCard run={latestRun} />
        </div>
      )}

      {/* Run History */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Run History
        </h2>
        {history.length > 0 ? (
          <div className="space-y-3">
            {history.map((run) => (
              <div
                key={run.runId}
                className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/30"
              >
                <div className="flex items-center gap-3">
                  {run.syncStatus === 'success' ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : run.syncStatus === 'failed' ? (
                    <XCircle className="h-4 w-4 text-red-500" />
                  ) : (
                    <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                  )}
                  <div>
                    <span className="text-sm font-mono">{run.runId.slice(0, 12)}...</span>
                    <p className="text-xs text-muted-foreground">
                      {new Date(run.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-emerald-400">{run.selectedCount} selected</span>
                  <span className="text-red-400">{run.rejectedCount} rejected</span>
                  <Badge variant="outline" className={syncStatusColors[run.syncStatus] || ''}>
                    {run.syncStatus}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Inbox className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No discovery runs yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Run the niche discovery pipeline to analyze markets and identify opportunities.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
